import React, { useState } from 'react';
import {
  Maximize,
  Search,
  X,
  Filter,
  Expand,
  Minimize,
  Eye,
  EyeOff,
  Palette,
  XCircle,
  Table,
  Settings,
  Upload,
  Save,
  FolderOpen,
  Undo2,
  Redo2,
  RotateCcw,
  Download,
  ChevronDown,
  Database
} from 'lucide-react';
import { LayoutDirection, PercentageDisplayMode } from '../types';
import packageJson from '../../package.json';

const HVG_LOGO_URL = "https://www.hvglaw.nl/wp-content/uploads/logo-in-browser-balk-3.jpg";

interface NavigationBarProps {
  // Chart controls
  onFitView: () => void;
  onExpandAll: () => void;
  onCollapseAll: () => void;
  onShowAll: () => void;
  
  // Layout controls
  layout: LayoutDirection;
  onLayoutChange: (layout: LayoutDirection) => void;
  
  // Depth filtering
  maxDepth: number | 'all';
  onDepthChange: (depth: number | 'all') => void;
  totalNodes: number;
  visibleNodes: number;
  
  // Display options
  showCountryFlags: boolean;
  onToggleCountryDisplay: () => void;
  percentageDisplayMode: PercentageDisplayMode;
  onTogglePercentageDisplay: () => void;
  showEntityTable?: boolean;
  onToggleEntityTable?: () => void;
  showSettingsPanel?: boolean;
  onToggleSettingsPanel?: () => void;

  // Search
  searchTerm: string;
  onSearchChange: (term: string) => void;
  onSearchClear: () => void;
  searchSuggestions: Array<{id: string, name: string, country: string}>;
  onSuggestionSelect: (nodeId: string) => void;
  showSuggestions: boolean;
  
  // Multi-select
  selectedNodeIds?: string[];
  onBulkHide?: () => void;
  onBulkColor?: (color: string | undefined) => void;
  onClearSelection?: () => void;

  // File operations
  onFileLoaded?: (file: File) => Promise<void>;
  onLoadProject?: () => void;
  onSaveProject?: () => void;
  hasProject?: boolean;

  // History
  onUndo?: () => void;
  onRedo?: () => void;
  onReset?: () => void;
  canUndo?: boolean;
  canRedo?: boolean;

  // Export
  onExportPNG?: (fullChart: boolean) => Promise<void>;
  onExportSVG?: (fullChart: boolean) => Promise<void>;
  onExportExcel?: () => Promise<void>;

  // Easter egg
  showSampleButton?: boolean;
  onLoadSampleData?: () => void;
  onLogoClick?: () => void;

  // State
  loading: boolean;
}

const NavigationBar: React.FC<NavigationBarProps> = ({
  onFitView,
  onExpandAll,
  onCollapseAll,
  onShowAll,
  maxDepth,
  onDepthChange,
  showEntityTable = false,
  onToggleEntityTable,
  showSettingsPanel = false,
  onToggleSettingsPanel,
  totalNodes,
  visibleNodes,
  searchTerm,
  onSearchChange,
  onSearchClear,
  searchSuggestions,
  onSuggestionSelect,
  showSuggestions,
  selectedNodeIds = [],
  onBulkHide,
  onBulkColor,
  onClearSelection,
  onFileLoaded,
  onLoadProject,
  onSaveProject,
  hasProject = false,
  onUndo,
  onRedo,
  onReset,
  canUndo = false,
  canRedo = false,
  onExportPNG,
  onExportSVG,
  onExportExcel,
  showSampleButton = false,
  onLoadSampleData,
  onLogoClick,
  loading
}) => {
  const [showBulkColorPicker, setShowBulkColorPicker] = useState(false);
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const [showExportMenu, setShowExportMenu] = React.useState(false);
  const exportMenuRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (exportMenuRef.current && !exportMenuRef.current.contains(event.target as Node)) {
        setShowExportMenu(false);
      }
    };
    if (showExportMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showExportMenu]);

  const handleExportClick = async (kind: 'PNG-window' | 'PNG-all' | 'SVG-window' | 'SVG-all' | 'Excel') => {
    setShowExportMenu(false);
    try {
      if (kind === 'PNG-window' && onExportPNG) await onExportPNG(false);
      else if (kind === 'PNG-all' && onExportPNG) await onExportPNG(true);
      else if (kind === 'SVG-window' && onExportSVG) await onExportSVG(false);
      else if (kind === 'SVG-all' && onExportSVG) await onExportSVG(true);
      else if (kind === 'Excel' && onExportExcel) await onExportExcel();
    } catch (err) {
      console.error('Export failed:', err);
    }
  };

  const bulkColorOptions = [
    { name: 'Standaard', value: undefined },
    { name: 'Blauw', value: '#3b82f6' },
    { name: 'Groen', value: '#10b981' },
    { name: 'Rood', value: '#ef4444' },
    { name: 'Paars', value: '#8b5cf6' },
    { name: 'Oranje', value: '#f59e0b' },
    { name: 'Roze', value: '#ec4899' },
    { name: 'Teal', value: '#14b8a6' },
  ];
  const depthOptions = [
    { value: 1, label: '1 niveau' },
    { value: 2, label: '2 niveaus' },
    { value: 3, label: '3 niveaus' },
    { value: 4, label: '4 niveaus' },
    { value: 5, label: '5 niveaus' },
    { value: 'all' as const, label: 'Alle niveaus' },
  ];

  return (
    <nav className="navigation-bar">
      <div className="nav-section nav-brand">
        <img
          src={HVG_LOGO_URL}
          alt="HVG Law"
          title={`v${packageJson.version}`}
          className="hvg-logo-small"
          onClick={onLogoClick}
          style={{ height: '32px', cursor: 'pointer' }}
        />
      </div>
      <div className="nav-divider" />

      <div className="nav-section">
        <input
          ref={fileInputRef}
          type="file"
          accept=".xlsx,.xls"
          onChange={async (e) => {
            const file = e.target.files?.[0];
            if (file && onFileLoaded) {
              await onFileLoaded(file);
            }
            if (fileInputRef.current) fileInputRef.current.value = '';
          }}
          style={{ display: 'none' }}
        />
        <button
          onClick={() => fileInputRef.current?.click()}
          className="btn btn-primary"
          disabled={loading}
          title="Excel bestand kiezen"
        >
          <Upload size={16} />
          Kies Excel
        </button>
        {onLoadProject && (
          <button
            onClick={onLoadProject}
            className="btn btn-secondary"
            disabled={loading}
            title="Project laden"
          >
            <FolderOpen size={16} />
            Laden
          </button>
        )}
        {onSaveProject && (
          <button
            onClick={onSaveProject}
            className="btn btn-secondary"
            disabled={loading || !hasProject}
            title="Project opslaan"
          >
            <Save size={16} />
            Opslaan
          </button>
        )}
        {showSampleButton && onLoadSampleData && (
          <button
            onClick={onLoadSampleData}
            className="btn btn-secondary"
            disabled={loading}
            title="Voorbeeldgegevens laden"
          >
            <Database size={16} />
          </button>
        )}
      </div>
      <div className="nav-divider" />

      <div className="nav-section">
        {onUndo && (
          <button
            onClick={onUndo}
            className="btn btn-secondary"
            disabled={loading || !canUndo}
            title="Ongedaan maken (Ctrl+Z)"
          >
            <Undo2 size={16} />
          </button>
        )}
        {onRedo && (
          <button
            onClick={onRedo}
            className="btn btn-secondary"
            disabled={loading || !canRedo}
            title="Opnieuw uitvoeren (Ctrl+Y)"
          >
            <Redo2 size={16} />
          </button>
        )}
        {onReset && (
          <button
            onClick={onReset}
            className="btn btn-secondary"
            disabled={loading}
            title="Weergave resetten"
          >
            <RotateCcw size={16} />
          </button>
        )}
      </div>
      <div className="nav-divider" />

      {/* Search Section */}
      <div className="nav-section">
        <div className="search-container">
          <div className="search-input-wrapper">
            <Search size={16} className="search-icon" />
            <input
              type="text"
              placeholder="Zoek bedrijf..."
              value={searchTerm}
              onChange={(e) => onSearchChange(e.target.value)}
              className="search-input"
              disabled={loading}
              style={{ width: '250px' }}
            />
            {searchTerm && (
              <button
                onClick={onSearchClear}
                className="search-clear"
                disabled={loading}
              >
                <X size={14} />
              </button>
            )}
          </div>
          
          {showSuggestions && searchSuggestions.length > 0 && (
            <div className="search-suggestions">
              {searchSuggestions.map((suggestion) => (
                <div
                  key={suggestion.id}
                  className="search-suggestion"
                  onClick={() => onSuggestionSelect(suggestion.id)}
                >
                  <span className="suggestion-name">{suggestion.name}</span>
                  <span className="suggestion-country">({suggestion.country})</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="nav-divider" />

      {/* Chart Controls */}
      <div className="nav-section">
        <button
          onClick={onFitView}
          className="btn btn-secondary"
          disabled={loading}
          title="Passend"
        >
          <Maximize size={16} />
          Passend
        </button>
        
        <button
          onClick={onExpandAll}
          className="btn btn-secondary"
          disabled={loading}
          title="Alles uitklappen"
        >
          <Expand size={16} />
        </button>
        
        <button
          onClick={onCollapseAll}
          className="btn btn-secondary"
          disabled={loading}
          title="Alles inklappen"
        >
          <Minimize size={16} />
        </button>
        
        <button
          onClick={onShowAll}
          className="btn btn-secondary"
          disabled={loading}
          title="Alle verborgen tonen"
        >
          <Eye size={16} />
        </button>
      </div>

      <div className="nav-divider" />

      {/* Display Toggles */}
      <div className="nav-section">
        <button
          onClick={onToggleSettingsPanel}
          className="btn btn-secondary"
          disabled={loading}
          aria-pressed={showSettingsPanel}
          title={showSettingsPanel ? 'Instellingen verbergen' : 'Instellingen tonen'}
          style={showSettingsPanel ? { background: '#eff6ff', borderColor: '#3b82f6', color: '#1d4ed8' } : undefined}
        >
          <Settings size={16} />
          <span style={{ fontSize: '11px', marginLeft: '4px', fontWeight: '600' }}>Instellingen</span>
        </button>

        <button
          onClick={onToggleEntityTable}
          className="btn btn-secondary"
          disabled={loading}
          aria-pressed={showEntityTable}
          title={showEntityTable ? 'Tabel verbergen' : 'Tabel tonen'}
          style={showEntityTable ? { background: '#eff6ff', borderColor: '#3b82f6', color: '#1d4ed8' } : undefined}
        >
          <Table size={16} />
          <span style={{ fontSize: '11px', marginLeft: '4px', fontWeight: '600' }}>Tabel</span>
        </button>
      </div>

      <div className="nav-divider" />

      {/* Depth Filter */}
      <div className="nav-section">
        <Filter size={16} />
        <select
          value={maxDepth}
          onChange={(e) => {
            const value = e.target.value;
            onDepthChange(value === 'all' ? 'all' : parseInt(value));
          }}
          className="select-input"
          disabled={loading}
        >
          {depthOptions.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        
        {totalNodes > 0 && (
          <span className="depth-info" style={{ fontSize: '12px', color: '#6b7280', whiteSpace: 'nowrap' }}>
            {maxDepth === 'all' ? (
              `${visibleNodes} bedrijven`
            ) : (
              `${visibleNodes}/${totalNodes}`
            )}
          </span>
        )}
      </div>

      {/* Bulk Action Bar - shown when multiple nodes are selected */}
      {selectedNodeIds.length > 1 && (
        <>
          <div className="nav-divider" />
          <div className="nav-section" style={{ background: '#eff6ff', borderRadius: '6px', padding: '4px 12px', gap: '8px' }}>
            <span style={{ fontSize: '13px', fontWeight: 600, color: '#1d4ed8', whiteSpace: 'nowrap' }}>
              {selectedNodeIds.length} geselecteerd
            </span>

            <button
              onClick={onBulkHide}
              className="btn btn-secondary"
              title="Selectie verbergen"
              style={{ fontSize: '12px', padding: '4px 8px' }}
            >
              <EyeOff size={14} />
              Verbergen
            </button>

            <div style={{ position: 'relative' }}>
              <button
                onClick={() => setShowBulkColorPicker(!showBulkColorPicker)}
                className="btn btn-secondary"
                title="Kleur wijzigen"
                style={{ fontSize: '12px', padding: '4px 8px' }}
              >
                <Palette size={14} />
                Kleur
              </button>

              {showBulkColorPicker && (
                <div
                  style={{
                    position: 'absolute',
                    top: '100%',
                    left: '0',
                    marginTop: '4px',
                    background: 'white',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    boxShadow: '0 8px 25px rgba(0, 0, 0, 0.15)',
                    padding: '8px',
                    display: 'grid',
                    gridTemplateColumns: 'repeat(2, 1fr)',
                    gap: '4px',
                    minWidth: '160px',
                    zIndex: 1000
                  }}
                >
                  {bulkColorOptions.map((color) => (
                    <button
                      key={color.name}
                      onClick={() => {
                        onBulkColor?.(color.value);
                        setShowBulkColorPicker(false);
                      }}
                      style={{
                        padding: '6px 10px',
                        border: '1px solid #e5e7eb',
                        borderRadius: '4px',
                        background: color.value || '#f8f9fa',
                        color: color.value ? 'white' : '#374151',
                        cursor: 'pointer',
                        fontSize: '11px',
                        transition: 'all 0.15s ease'
                      }}
                    >
                      {color.name}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <button
              onClick={onClearSelection}
              className="btn btn-secondary"
              title="Selectie wissen"
              style={{ fontSize: '12px', padding: '4px 8px' }}
            >
              <XCircle size={14} />
            </button>
          </div>
        </>
      )}

      {(onExportPNG || onExportSVG || onExportExcel) && (
        <div ref={exportMenuRef} style={{ position: 'relative', display: 'inline-block', marginLeft: 'auto' }}>
          <button
            onClick={() => setShowExportMenu(!showExportMenu)}
            className="btn btn-secondary"
            disabled={loading || !hasProject}
            title="Exporteren"
          >
            <Download size={16} />
            Exporteren
            <ChevronDown size={14} style={{ marginLeft: '4px' }} />
          </button>
          {showExportMenu && (
            <div style={{
              position: 'absolute',
              top: '100%',
              right: 0,
              marginTop: '4px',
              backgroundColor: 'white',
              border: '1px solid #e5e7eb',
              borderRadius: '6px',
              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
              zIndex: 1000,
              minWidth: '200px',
              padding: '4px 0'
            }}>
              {onExportPNG && (
                <div style={{ padding: '4px 0', borderBottom: '1px solid #e5e7eb' }}>
                  <div style={{ padding: '4px 12px', fontSize: '11px', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase' }}>PNG</div>
                  <button onClick={() => handleExportClick('PNG-window')} className="export-menu-item">PNG - Venster</button>
                  <button onClick={() => handleExportClick('PNG-all')} className="export-menu-item">PNG - Alles</button>
                </div>
              )}
              {onExportSVG && (
                <div style={{ padding: '4px 0', borderBottom: '1px solid #e5e7eb' }}>
                  <div style={{ padding: '4px 12px', fontSize: '11px', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase' }}>SVG</div>
                  <button onClick={() => handleExportClick('SVG-window')} className="export-menu-item">SVG - Venster</button>
                  <button onClick={() => handleExportClick('SVG-all')} className="export-menu-item">SVG - Alles</button>
                </div>
              )}
              {onExportExcel && (
                <div style={{ padding: '4px 0' }}>
                  <div style={{ padding: '4px 12px', fontSize: '11px', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase' }}>Excel</div>
                  <button onClick={() => handleExportClick('Excel')} className="export-menu-item">Excel (.xlsx)</button>
                </div>
              )}
            </div>
          )}
        </div>
      )}

    </nav>
  );
};

export default NavigationBar;
